import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Trash2, X, FileText, Upload,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const uid = () => Math.random().toString(36).slice(2, 10);

const ROLE_LABEL = { company_admin: 'Admins', dispatcher: 'Dispatchers', driver: 'Drivers' };

const emptyCourse = {
  title: '', description: '', modules: [], quiz: [],
  passing_score: 70, assigned_roles: ['driver'], status: 'draft',
  prerequisites: [], max_attempts: '', ceu_credits: '', due_date: '',
  assigned_cohort_ids: [],
};

const AudiencePicker = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {Object.entries(ROLE_LABEL).map(([r, label]) => {
      const on = value.includes(r);
      return (
        <button
          key={r} type="button"
          onClick={() => onChange(on ? value.filter((x) => x !== r) : [...value, r])}
          data-testid={`training-role-${r}`}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${on ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

const ModuleRow = ({ module, onChange, onRemove, courseId }) => {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !courseId) return;
    const fd = new FormData();
    fd.append('file', f);
    setUploading(true);
    try {
      const r = await axios.post(`${API}/training/courses/${courseId}/upload-pdf`, fd, { withCredentials: true });
      onChange({ ...module, file_id: r.data.file_id, title: module.title || r.data.file_name });
      toast.success('PDF attached');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2" data-testid={`module-row-${module.id}`}>
      <div className="flex items-center gap-2">
        <select value={module.type} onChange={(e) => onChange({ ...module, type: e.target.value })}
          className="text-xs bg-white border border-slate-300 rounded px-2 py-1 focus:outline-none">
          <option value="pdf">PDF</option>
          <option value="video">Video URL</option>
          <option value="text">Text</option>
        </select>
        <input placeholder="Module title" value={module.title}
          onChange={(e) => onChange({ ...module, title: e.target.value })}
          className="flex-1 text-sm px-2 py-1 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-[#002FA7]" />
        <button type="button" onClick={onRemove} className="text-red-600 hover:text-red-800">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {module.type === 'pdf' && (
        <div className="flex items-center gap-2">
          {module.file_id ? (
            <span className="text-xs text-emerald-700 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Attached</span>
          ) : (
            <label className="text-xs bg-white border border-slate-300 rounded px-2 py-1 cursor-pointer flex items-center gap-1 hover:bg-slate-100">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {courseId ? 'Upload PDF' : 'Save course first'}
              <input type="file" accept="application/pdf" onChange={upload} disabled={!courseId || uploading} className="hidden" />
            </label>
          )}
        </div>
      )}
      {module.type === 'video' && (
        <input placeholder="https://youtube.com/watch?v=..." value={module.video_url || ''}
          onChange={(e) => onChange({ ...module, video_url: e.target.value })}
          className="w-full text-xs px-2 py-1 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-[#002FA7]" />
      )}
      {module.type === 'text' && (
        <textarea placeholder="Module content..." rows={3} value={module.content || ''}
          onChange={(e) => onChange({ ...module, content: e.target.value })}
          className="w-full text-xs px-2 py-1 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-[#002FA7]" />
      )}
    </div>
  );
};

const QuizRow = ({ q, onChange, onRemove, idx }) => (
  <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2" data-testid={`quiz-row-${q.id}`}>
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold text-slate-500">Q{idx + 1}</span>
      <input placeholder="Question" value={q.question}
        onChange={(e) => onChange({ ...q, question: e.target.value })}
        className="flex-1 text-sm px-2 py-1 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-[#002FA7]" />
      <button type="button" onClick={onRemove} className="text-red-600 hover:text-red-800">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
    <div className="space-y-1 pl-6">
      {q.choices.map((c, i) => (
        <div key={i} className="flex items-center gap-2">
          <input type="radio" checked={q.correct_index === i}
            onChange={() => onChange({ ...q, correct_index: i })}
            className="accent-[#002FA7]"
            data-testid={`quiz-correct-${q.id}-${i}`} />
          <input placeholder={`Choice ${i + 1}`} value={c}
            onChange={(e) => {
              const choices = [...q.choices]; choices[i] = e.target.value;
              onChange({ ...q, choices });
            }}
            className="flex-1 text-xs px-2 py-1 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-[#002FA7]" />
          {q.choices.length > 2 && (
            <button type="button" onClick={() => {
              const choices = q.choices.filter((_, x) => x !== i);
              onChange({ ...q, choices, correct_index: Math.min(q.correct_index, choices.length - 1) });
            }} className="text-slate-400 hover:text-red-600">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      ))}
      {q.choices.length < 6 && (
        <button type="button" onClick={() => onChange({ ...q, choices: [...q.choices, ''] })}
          className="text-xs text-[#002FA7] hover:text-[#00247D] ml-4">+ Add choice</button>
      )}
    </div>
  </div>
);

const TrainingCourseEditor = ({ course, allCourses = [], onClose, onSaved }) => {
  const [form, setForm] = useState(() => {
    const base = course.id ? { ...course } : { ...emptyCourse, id: null };
    base.max_attempts = base.max_attempts == null ? '' : String(base.max_attempts);
    base.ceu_credits = base.ceu_credits == null ? '' : String(base.ceu_credits);
    base.prerequisites = Array.isArray(base.prerequisites) ? base.prerequisites : [];
    base.assigned_cohort_ids = Array.isArray(base.assigned_cohort_ids) ? base.assigned_cohort_ids : [];
    if (base.due_date && typeof base.due_date === 'string') {
      base.due_date = base.due_date.slice(0, 16);
    } else if (!base.due_date) {
      base.due_date = '';
    }
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [cohorts, setCohorts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/cohorts`, { withCredentials: true })
      .then((r) => setCohorts(r.data))
      .catch(() => setCohorts([]));
  }, []);

  const addModule = () => setForm({ ...form, modules: [...form.modules, { id: uid(), type: 'pdf', title: '' }] });
  const addQuestion = () => setForm({
    ...form,
    quiz: [...form.quiz, { id: uid(), question: '', choices: ['', ''], correct_index: 0 }],
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description || null,
        modules: form.modules, quiz: form.quiz,
        passing_score: parseInt(form.passing_score, 10) || 0,
        assigned_roles: form.assigned_roles,
        assigned_cohort_ids: form.assigned_cohort_ids || [],
        status: form.status,
        prerequisites: form.prerequisites || [],
        max_attempts: form.max_attempts === '' ? null : parseInt(form.max_attempts, 10),
        ceu_credits: form.ceu_credits === '' ? null : parseFloat(form.ceu_credits),
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      };
      if (form.id) {
        await axios.put(`${API}/training/courses/${form.id}`, payload, { withCredentials: true });
        toast.success('Course updated');
      } else {
        const r = await axios.post(`${API}/training/courses`, payload, { withCredentials: true });
        setForm({ ...form, id: r.data.id });
        toast.success('Course created — you can now upload PDFs');
      }
      onSaved();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        toast.error('Your session expired — please log in again.');
      } else {
        toast.error(err.response?.data?.detail || 'Save failed');
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="course-editor">
      <div className="bg-white rounded-md shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {form.id ? 'Edit course' : 'New course'}
          </h3>
          <button onClick={onClose} data-testid="course-editor-close" className="p-1 rounded hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <form onSubmit={save} className="px-6 py-5 space-y-5">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Course title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="course-title"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Description</label>
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="course-description"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Modules</label>
              <button type="button" onClick={addModule} data-testid="add-module"
                className="text-xs text-[#002FA7] hover:text-[#00247D] font-semibold">+ Add module</button>
            </div>
            <div className="space-y-2">
              {form.modules.map((m) => (
                <ModuleRow key={m.id} module={m} courseId={form.id}
                  onChange={(next) => setForm({ ...form, modules: form.modules.map((x) => x.id === m.id ? next : x) })}
                  onRemove={() => setForm({ ...form, modules: form.modules.filter((x) => x.id !== m.id) })} />
              ))}
              {form.modules.length === 0 && <p className="text-xs text-slate-500 italic">No modules yet.</p>}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Quiz</label>
              <button type="button" onClick={addQuestion} data-testid="add-question"
                className="text-xs text-[#002FA7] hover:text-[#00247D] font-semibold">+ Add question</button>
            </div>
            <div className="space-y-2">
              {form.quiz.map((q, idx) => (
                <QuizRow key={q.id} q={q} idx={idx}
                  onChange={(next) => setForm({ ...form, quiz: form.quiz.map((x) => x.id === q.id ? next : x) })}
                  onRemove={() => setForm({ ...form, quiz: form.quiz.filter((x) => x.id !== q.id) })} />
              ))}
              {form.quiz.length === 0 && <p className="text-xs text-slate-500 italic">No quiz questions yet.</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Passing score (%)</label>
              <input type="number" min="0" max="100" value={form.passing_score}
                onChange={(e) => setForm({ ...form, passing_score: e.target.value })}
                data-testid="course-passing-score"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="course-status"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-[#002FA7] focus:outline-none">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Assigned audience</label>
            <div className="mt-2">
              <AudiencePicker value={form.assigned_roles}
                onChange={(next) => setForm({ ...form, assigned_roles: next })} />
            </div>
          </div>

          {cohorts.length > 0 && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Assigned cohorts</label>
              <p className="text-xs text-slate-500 mt-0.5">Cohort members automatically get access alongside the role selection above.</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="cohort-picker">
                {cohorts.map((c) => {
                  const on = (form.assigned_cohort_ids || []).includes(c.id);
                  return (
                    <button key={c.id} type="button"
                      onClick={() => setForm({
                        ...form,
                        assigned_cohort_ids: on
                          ? form.assigned_cohort_ids.filter((x) => x !== c.id)
                          : [...(form.assigned_cohort_ids || []), c.id],
                      })}
                      data-testid={`cohort-chip-${c.id}`}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${on ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                    >
                      {c.name} · {(c.member_user_ids || []).length}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Max attempts</label>
              <input type="number" min="1" max="50" placeholder="unlimited"
                value={form.max_attempts}
                onChange={(e) => setForm({ ...form, max_attempts: e.target.value })}
                data-testid="course-max-attempts"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">CEU credits</label>
              <input type="number" min="0" step="0.1" placeholder="e.g. 1.5"
                value={form.ceu_credits}
                onChange={(e) => setForm({ ...form, ceu_credits: e.target.value })}
                data-testid="course-ceu-credits"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Deadline</label>
              <input type="datetime-local"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="course-due-date"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
          </div>

          {allCourses.length > 0 && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Prerequisites</label>
              <p className="text-xs text-slate-500 mt-0.5">Learners must have passed these courses before enrolling.</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {allCourses.filter((c) => c.id !== form.id).map((c) => {
                  const on = (form.prerequisites || []).includes(c.id);
                  return (
                    <button key={c.id} type="button"
                      onClick={() => setForm({
                        ...form,
                        prerequisites: on
                          ? form.prerequisites.filter((x) => x !== c.id)
                          : [...(form.prerequisites || []), c.id],
                      })}
                      data-testid={`prereq-${c.id}`}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${on ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                    >
                      {c.title}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Close</button>
            <button type="submit" disabled={saving} data-testid="course-save"
              className="px-4 py-2 bg-[#002FA7] text-white text-sm font-semibold rounded-md hover:bg-[#00247D] disabled:opacity-50 flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {form.id ? 'Save changes' : 'Create course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrainingCourseEditor;
